/**
 * Who is playing, on this device. Identity is a random id minted on first run plus a
 * display name the player types when they first post a score. Both live in localStorage;
 * the server just trusts the id (a leaderboard for family and friends, not a bank).
 */
const KEY = 'labu.player';

export interface Player {
  id: string;
  name: string;
  sound: boolean;
  haptics: boolean;
  /** show the gesture a special card wants, on the card */
  gestureHints: boolean;
  /** practice: the chant word is highlighted; rounds don't post to the leaderboard */
  practice: boolean;
}

function newId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function loadPlayer(): Player {
  const base: Player = { id: newId(), name: '', sound: true, haptics: true, gestureHints: true, practice: false };
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Player> | null;
    if (saved?.id) return { ...base, ...saved };
  } catch {
    /* no storage */
  }
  savePlayer(base);
  return base;
}

export function savePlayer(p: Player): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* no storage */
  }
}

export interface LocalBest {
  score: number;
  mistakes: number;
  at: number;
}

export function loadBest(game: string): LocalBest | null {
  try {
    return JSON.parse(localStorage.getItem(`labu.best.${game}`) ?? 'null') as LocalBest | null;
  } catch {
    return null;
  }
}

/** record a finished round; true if it is a new personal best */
export function recordBest(game: string, score: number, mistakes: number, lowerIsBetter = true): boolean {
  const prev = loadBest(game);
  const better = !prev || (lowerIsBetter ? score < prev.score : score > prev.score);
  if (better) {
    try {
      localStorage.setItem(`labu.best.${game}`, JSON.stringify({ score, mistakes, at: Date.now() } satisfies LocalBest));
    } catch {
      /* no storage */
    }
  }
  return better;
}
